/**
 * BuilderInteractionBand — single, standardized UX surface for every Claude
 * Code interactive prompt (permission, choice, confirm, free-form input).
 *
 * Reads ``interaction`` payload from BUILDER_INPUT_WAIT (when present);
 * falls back to legacy ``promptSnippet`` parsing for back-compat. All
 * variants render through this one component:
 *
 *   - kind="permission" → Accept/Deny + optional Always-allow + custom input
 *   - kind="choice"     → Stacked option buttons, no input
 *   - kind="confirm"    → Confirm/Cancel
 *   - kind="input"      → Free-form input field only
 *   - hybrid            → Options + custom input together
 *
 * Visual: severity strip (info/warn/danger), tool icon, title + description,
 * structured diff/command preview, action row with shortcuts, countdown badge.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Send, Check, X as XIcon,
  FileEdit, FilePlus, Terminal, Wrench, AlertTriangle, Info, ShieldAlert, Clock,
} from 'lucide-react';
import { postUniversalBuilderCliLine } from '@stepflow/ui-shared/services/builderCliApi.js';

const TOOL_ICONS = {
  Edit: FileEdit,
  Write: FilePlus,
  MultiEdit: FileEdit,
  Bash: Terminal,
};

const SEVERITY_BAR = {
  info: 'bg-transparent',
  warn: 'bg-amber-400',
  danger: 'bg-rose-500',
};

const SEVERITY_ICON = {
  info: { Icon: Info, className: 'text-gray-500' },
  warn: { Icon: AlertTriangle, className: 'text-amber-600' },
  danger: { Icon: ShieldAlert, className: 'text-rose-600' },
};

const TONE_BTN = {
  primary: 'bg-emerald-600 hover:bg-emerald-700 text-white focus-visible:ring-emerald-500',
  danger: 'bg-rose-600 hover:bg-rose-700 text-white focus-visible:ring-rose-500',
  secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus-visible:ring-blue-500',
  ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 focus-visible:ring-gray-400',
};

const DEFAULT_PERMISSION_OPTIONS = [
  { value: 'y', label: 'Accept', tone: 'primary', shortcut: 'y' },
  { value: 'n', label: 'Deny', tone: 'danger', shortcut: 'n' },
];

/**
 * Parse legacy "ToolName: {json...}" snippet for back-compat when the backend
 * doesn't send a structured ``interaction`` payload yet.
 */
function legacyParse(snippet) {
  if (!snippet || typeof snippet !== 'string') return null;
  const colon = snippet.indexOf(':');
  if (colon < 0) return { kind: 'permission', title: snippet };
  const tool = snippet.slice(0, colon).trim();
  const rest = snippet.slice(colon + 1).trim();
  const fp = rest.match(/"file_path"\s*:\s*"([^"]+)"/);
  const cmd = rest.match(/"command"\s*:\s*"([^"]+)/);
  const oldS = rest.match(/"old_string"\s*:\s*"([^"\\]+)/);
  const newS = rest.match(/"new_string"\s*:\s*"([^"\\]+)/);

  if (tool === 'Edit' && fp) {
    return {
      kind: 'permission', tool, severity: 'warn',
      title: `Edit ${fp[1].split('/').pop()}`,
      description: fp[1],
      diff: { filePath: fp[1], oldString: oldS ? oldS[1] : '', newString: newS ? newS[1] : '' },
    };
  }
  if (tool === 'Write' && fp) {
    return {
      kind: 'permission', tool, severity: 'warn',
      title: `Create ${fp[1].split('/').pop()}`,
      description: fp[1],
      diff: { filePath: fp[1], newString: newS ? newS[1] : '' },
    };
  }
  if (tool === 'Bash' && cmd) {
    return {
      kind: 'permission', tool, severity: 'warn',
      title: 'Run shell command',
      command: cmd[1],
    };
  }
  return { kind: 'permission', tool: tool || 'Tool', title: tool || 'Builder needs permission', description: rest.slice(0, 200) };
}

function CountdownBadge({ expiresAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(ms) || ms <= 0) return null;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium text-gray-600 shadow-sm">
      <Clock className="h-2.5 w-2.5" aria-hidden />
      {m > 0 ? `${m}m ${s}s` : `${s}s`}
    </span>
  );
}

function DiffPreview({ diff }) {
  const [expanded, setExpanded] = useState(false);
  if (!diff) return null;
  const oldLines = String(diff.oldString || '').split('\n');
  const newLines = String(diff.newString || '').split('\n');
  const truncate = !expanded && (oldLines.length > 4 || newLines.length > 4);
  const oldShown = truncate ? oldLines.slice(0, 4) : oldLines;
  const newShown = truncate ? newLines.slice(0, 4) : newLines;
  return (
    <div className="mt-1 overflow-hidden rounded-md border border-gray-300/60 bg-white/70 font-mono text-[11px] leading-relaxed">
      {oldShown.length > 0 && oldShown[0] !== '' ? (
        <div className="bg-rose-50/60">
          {oldShown.map((l, i) => (
            <div key={`o${i}`} className="px-2 py-0.5 text-rose-800">
              <span className="mr-2 select-none text-rose-400">−</span>{l || ' '}
            </div>
          ))}
        </div>
      ) : null}
      {newShown.length > 0 && newShown[0] !== '' ? (
        <div className="bg-emerald-50/60">
          {newShown.map((l, i) => (
            <div key={`n${i}`} className="px-2 py-0.5 text-emerald-800">
              <span className="mr-2 select-none text-emerald-500">+</span>{l || ' '}
            </div>
          ))}
        </div>
      ) : null}
      {truncate ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full border-t border-gray-200 bg-gray-50 px-2 py-1 text-[10px] font-medium text-gray-600 hover:bg-gray-100"
        >
          Show more ({oldLines.length + newLines.length} lines)
        </button>
      ) : null}
    </div>
  );
}

function CommandPreview({ command }) {
  if (!command) return null;
  return (
    <pre className="mt-1 max-h-24 overflow-auto rounded-md border border-gray-300/60 bg-gray-900 px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-emerald-200">
      $ {command}
    </pre>
  );
}

/**
 * @param {Object} props
 * @param {string} props.networkId
 * @param {string} props.runId
 * @param {string} [props.poolId] — pool channel for cross-user RESOLVED broadcast
 * @param {string} [props.waitEventId] — eventId of the active BUILDER_INPUT_WAIT
 *   envelope. Sent with the response so the cross-user RESOLVED broadcast
 *   includes it; peer UIs match RESOLVED → WAIT by eventId (not just runId)
 *   to handle multi-turn sessions where the same runId hosts many WAITs.
 * @param {Object} [props.interaction] — structured payload from backend
 * @param {string} [props.promptSnippet] — legacy fallback
 * @param {() => void} props.onHandled
 * @param {boolean} [props.readOnly] — if true, viewer can see the prompt but not act
 *   (used for non-owner / non-elevated workspace members per RBAC).
 */
export default function BuilderInteractionBand({
  networkId, runId, poolId, waitEventId, interaction, promptSnippet, onHandled, readOnly = false,
}) {
  const [busy, setBusy] = useState(false);
  const [busyValue, setBusyValue] = useState('');
  const [error, setError] = useState('');
  const [customLine, setCustomLine] = useState('');
  /** Selected option values for multi-select choice mode. */
  const [selectedValues, setSelectedValues] = useState(() => new Set());
  const inputRef = useRef(null);

  // Latest schema version we understand. If the backend bumps the version
  // beyond this, treat the payload as unknown and fall back to legacy parsing
  // (forward-compat: never crash on an unfamiliar shape).
  const SUPPORTED_SCHEMA_VERSION = 1;

  const spec = useMemo(() => {
    if (interaction && typeof interaction === 'object') {
      const v = Number(interaction.schemaVersion ?? 1);
      if (Number.isFinite(v) && v <= SUPPORTED_SCHEMA_VERSION) return interaction;
      // Unknown future version — best-effort: keep title/options if shape matches,
      // otherwise treat as opaque permission prompt.
      const looksUsable = (interaction.title || interaction.options || interaction.kind);
      if (looksUsable) return interaction;
    }
    return legacyParse(promptSnippet) || { kind: 'permission' };
  }, [interaction, promptSnippet]);

  const kind = spec.kind || 'permission';
  const tool = spec.tool || '';
  const severity = spec.severity || 'info';
  const options = Array.isArray(spec.options) && spec.options.length > 0
    ? spec.options
    : (kind === 'permission' ? DEFAULT_PERMISSION_OPTIONS
       : kind === 'confirm' ? [
           { value: 'y', label: 'Confirm', tone: 'primary' },
           { value: 'n', label: 'Cancel', tone: 'secondary' },
         ]
       : []);
  // Choice mode keeps the input visible too so the user can answer with
  // freeform text (the backend treats freeform as denial-with-message which
  // claude reads as the user's answer). Permission/confirm hide the custom
  // input by default.
  const allowCustom = spec.allowCustom !== false;
  const isMultiSelect = kind === 'choice' && spec.multiSelect === true;
  const stackVertical = options.length > 2 || isMultiSelect;
  // Multi-question batch metadata (set by backend for AskUserQuestion batches).
  const questionIndex = Number.isFinite(spec.questionIndex) ? spec.questionIndex : null;
  const questionTotal = Number.isFinite(spec.questionTotal) ? spec.questionTotal : null;
  const isBatch = questionTotal !== null && questionTotal > 1;

  const ToolIcon = (tool && TOOL_ICONS[tool]) || Wrench;
  const SevIcon = SEVERITY_ICON[severity]?.Icon || Info;
  const sevClass = SEVERITY_ICON[severity]?.className || 'text-gray-500';

  const sendLine = useCallback(async (value) => {
    if (busy || readOnly || !networkId || !runId) return;
    setBusy(true);
    setBusyValue(String(value));
    setError('');
    try {
      const body = { runId, line: value };
      if (poolId) body.poolId = poolId;
      if (waitEventId) body.waitEventId = waitEventId;
      await postUniversalBuilderCliLine(networkId, body);
      onHandled?.();
    } catch (e) {
      const st = typeof e?.httpStatus === 'number' ? e.httpStatus : null;
      const code = typeof e?.errorCode === 'string' ? e.errorCode : '';
      if (st === 409 || code === 'CliInputAlreadyCaptured') {
        onHandled?.();
      } else {
        setError(e.message || String(e));
      }
    } finally {
      setBusy(false);
      setBusyValue('');
    }
  }, [busy, readOnly, networkId, runId, poolId, waitEventId, onHandled]);

  // Keyboard shortcuts (only fire when input isn't focused, and only when authorized).
  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === 'Enter' && spec.defaultOption) {
        e.preventDefault();
        void sendLine(spec.defaultOption);
        return;
      }
      const m = options.find((o) => o.shortcut && o.shortcut.toLowerCase() === e.key.toLowerCase());
      if (m) {
        e.preventDefault();
        void sendLine(m.value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [options, spec.defaultOption, sendLine, readOnly]);

  const submitCustom = () => {
    const v = customLine.trim();
    if (v) void sendLine(v);
  };

  // Choice mode: 3+ options need full-width stacked buttons with wrapped labels.
  // Description is multi-line (not truncated). Buttons take precedence over compact inline layout.
  const isChoiceMode = stackVertical;

  return (
    <div className="flex w-full flex-col gap-2 text-sm text-gray-700">
      {/* Severity bar */}
      <div className={`-mx-3.5 -mt-2 mb-0 h-0.5 ${SEVERITY_BAR[severity]}`} />

      {/* Header: icon + title + countdown */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <div className="mt-0.5 flex shrink-0 items-center gap-1.5">
            <SevIcon className={`h-4 w-4 ${sevClass}`} aria-hidden />
            {tool ? <ToolIcon className="h-4 w-4 text-gray-500" aria-hidden /> : null}
          </div>
          <div className="min-w-0 flex-1">
            {spec.title ? (
              <div className={isChoiceMode ? 'font-medium text-gray-900' : 'truncate font-medium text-gray-900'}>
                {spec.title}
              </div>
            ) : (
              <div className="font-medium text-gray-900">Builder needs input</div>
            )}
            {spec.description ? (
              <div className={isChoiceMode ? 'mt-0.5 text-[12px] leading-snug text-gray-600' : 'truncate text-[11px] text-gray-600'}>
                {spec.description}
              </div>
            ) : null}
          </div>
        </div>
        <CountdownBadge expiresAt={spec.expiresAt} />
      </div>

      {/* Read-only viewers (RBAC: not room owner, not workspace OWNER/ADMIN/CREATOR)
          see the prompt for awareness but cannot act. */}
      {readOnly ? (
        <div className="flex items-center gap-2 rounded-md bg-white/60 px-2.5 py-1.5 text-[11px] text-gray-600 border border-gray-300/60">
          <Loader2 className="h-3 w-3 animate-spin text-gray-400" aria-hidden />
          <span>Awaiting decision from a room owner or workspace admin…</span>
        </div>
      ) : null}

      {/* Action area. For ≤2 options: inline row with input. For 3+ options:
          full-width stacked buttons (scroll capped) + input on its own row.
          Multi-select shows checkboxes + a Submit button.
          Diff/command/context are intentionally NOT shown — the builder stream
          above already renders them in full.
          Hidden entirely for read-only viewers (RBAC). */}
      {!readOnly && isChoiceMode && isMultiSelect ? (
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full max-h-64 flex-col gap-1.5 overflow-y-auto">
            {options.map((opt, idx) => {
              const checked = selectedValues.has(String(opt.value));
              const letter = String.fromCharCode(65 + idx);
              return (
                <label
                  key={String(opt.value)}
                  className={`flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-xs font-medium shadow-sm transition-colors cursor-pointer ${
                    checked
                      ? 'border-emerald-300 bg-emerald-50/60 text-emerald-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  } ${busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={() => {
                      setSelectedValues((prev) => {
                        const next = new Set(prev);
                        if (next.has(String(opt.value))) next.delete(String(opt.value));
                        else next.add(String(opt.value));
                        return next;
                      });
                    }}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <kbd className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-current/40 text-[10px] font-mono opacity-80">
                    {opt.shortcut || letter}
                  </kbd>
                  <div className="flex-1 min-w-0">
                    <div className="whitespace-normal break-words">{opt.label}</div>
                    {opt.description ? (
                      <div className="mt-0.5 text-[11px] font-normal opacity-75 whitespace-normal break-words">
                        {opt.description}
                      </div>
                    ) : null}
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-gray-500">
              {selectedValues.size === 0
                ? 'Select one or more options'
                : `${selectedValues.size} selected`}
              {isBatch ? ` · question ${questionIndex + 1} of ${questionTotal}` : ''}
            </span>
            <button
              type="button"
              onClick={() => {
                if (selectedValues.size === 0) return;
                // Send selections as a comma-separated list. Order matches
                // option order so the agent receives a stable answer.
                const ordered = options
                  .map((o) => String(o.value))
                  .filter((v) => selectedValues.has(v));
                void sendLine(ordered.join(", "));
              }}
              disabled={busy || selectedValues.size === 0}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Check className="h-3 w-3" aria-hidden />}
              Submit
            </button>
          </div>
        </div>
      ) : !readOnly && isChoiceMode ? (
        <div className="flex w-full flex-col gap-1.5">
          {isBatch ? (
            <div className="text-[11px] text-gray-500">
              Question {questionIndex + 1} of {questionTotal}
            </div>
          ) : null}
          <div className="flex w-full max-h-64 flex-col gap-1.5 overflow-y-auto">
            {options.map((opt, idx) => {
              const isBusyHere = busy && busyValue === String(opt.value);
              const letter = String.fromCharCode(65 + idx);
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => void sendLine(opt.value)}
                  disabled={busy}
                  className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-xs font-medium shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed ${TONE_BTN[opt.tone] || TONE_BTN.secondary}`}
                >
                  <kbd className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-current/40 text-[10px] font-mono opacity-80">
                    {opt.shortcut || letter}
                  </kbd>
                  <div className="flex-1 min-w-0">
                    <div className="whitespace-normal break-words">{opt.label}</div>
                    {opt.description ? (
                      <div className="mt-0.5 text-[11px] font-normal opacity-75 whitespace-normal break-words">
                        {opt.description}
                      </div>
                    ) : null}
                  </div>
                  {isBusyHere ? <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin" aria-hidden /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : !readOnly ? (
        <div className="flex flex-wrap items-center gap-2">
          {options.map((opt) => {
            const isBusyHere = busy && busyValue === String(opt.value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => void sendLine(opt.value)}
                disabled={busy}
                className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed ${TONE_BTN[opt.tone] || TONE_BTN.secondary}`}
              >
                {isBusyHere ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> :
                  opt.tone === 'primary' ? <Check className="h-3 w-3" aria-hidden /> :
                  opt.tone === 'danger' ? <XIcon className="h-3 w-3" aria-hidden /> : null}
                <span>{opt.label}</span>
                {opt.shortcut ? (
                  <kbd className="ml-1 rounded border border-current/30 px-1 text-[9px] font-mono opacity-70">{opt.shortcut}</kbd>
                ) : null}
              </button>
            );
          })}
          {allowCustom && !isChoiceMode ? (
            <div className="ml-1 flex flex-1 min-w-[200px] items-center gap-1.5">
              <input
                ref={inputRef}
                type="text"
                value={customLine}
                onChange={(e) => setCustomLine(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customLine.trim()) {
                    e.preventDefault();
                    submitCustom();
                  }
                }}
                disabled={busy}
                placeholder={spec.customPlaceholder || 'Custom reply…'}
                className="h-8 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-40"
              />
              <button
                type="button"
                onClick={submitCustom}
                disabled={busy || !customLine.trim()}
                aria-label="Send custom response"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="h-3 w-3" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Choice-mode custom input: full-width row below the stacked buttons. */}
      {!readOnly && allowCustom && isChoiceMode ? (
        <div className="flex w-full items-center gap-1.5">
          <input
            ref={inputRef}
            type="text"
            value={customLine}
            onChange={(e) => setCustomLine(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customLine.trim()) {
                e.preventDefault();
                submitCustom();
              }
            }}
            disabled={busy}
            placeholder={spec.customPlaceholder || 'Custom reply…'}
            className="h-8 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-40"
          />
          <button
            type="button"
            onClick={submitCustom}
            disabled={busy || !customLine.trim()}
            aria-label="Send custom response"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="h-3 w-3" aria-hidden />
          </button>
        </div>
      ) : null}

      {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
    </div>
  );
}
